/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    useAppStore,
    useEPGStore,
    useTVStore,
    useVODStore,
} from '@/shared/store'
import Channel from '@/shared/classes/channels/channel'
import { useEPGCache } from '@/shared/cache/hooks/useEPGCache'
import { useTVRepository } from '@/features/repositories/useTVRepository'
import {
    IS_EPG_CACHE_CHECKED,
    IS_TV_CACHE_CHECKED,
    MAX_CHANNELS_FETCH_AMOUNT,
    MILIS_6h,
    PRESENT_FOLLOWING_REFRESH_INTERVAL,
} from '@/shared/constants'
import {
    getFromLocalStorage,
    lastTimeStart,
    refreshTokenKeyword,
    removeFromLocalStorage,
    saveToLocalStorage,
    sessionTokenLsKeyword,
} from '@/shared/localStorage/localStorageRepository'
import { useEPGRepository } from '@/features/repositories/useEPGRepository'
import { getProgramsForAllChannels } from '@/shared/classes/epg/helpers/getMissingPrograms'
import { useSetTVAndEpgToStateAndCache } from '@/shared/hooks/useSetEpgToStateAndCache'
import { ExtendedEpgElement, RawEpgObject } from '@/types/epg/Epg'
import { getErrorMessage } from '@/shared/lib'
import {
    useCacheAutoRefresh,
    useCategoriesForFiltering,
    useConfig,
    useSetCategoryIfAllGenres,
} from '@/shared/hooks'
import { shallow } from 'zustand/shallow'
import { selectSetClearVod } from '@/shared/store/storeSelectors/vodSelectors'
import { useVodItems } from '@/features/VOD/hooks/useVodItems'
import useFavoritesChannels from '@/features/favorites/useFavoritesChannels'

const ComponentsRenderContext = createContext<any>(null)

const createInitialExtendedEpg = (channels: Channel[]) => {
    const initialEmptyProgramsEpg: ExtendedEpgElement[] = []
    channels.forEach((channel: Channel) => {
        const { id, lcn, name, icon } = channel
        const fullObject = {
            channelIcon: icon,
            channelName: name,
            channelNumber: parseInt(lcn),
            channelId: id.toString(),
            programs: [],
        }
        initialEmptyProgramsEpg.push(fullObject)
    })
    return initialEmptyProgramsEpg
}

export const ComponentsRenderProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const [isComponentsReadyToRender, setIsComponentsReadyToRender] =
        useState(false)
    const { getEpgFromCache, getTvFromCache, clearCache } = useEPGCache()
    const { channelsData, setChannelsData, isStoreHydrated, clearTvStore } =
        useTVStore(
            (state) => ({
                channelsData: state.channelsData,
                setChannelsData: state.setChannelsData,
                isStoreHydrated: state.isStoreHydrated,
                clearTvStore: state.clearTvStore,
            }),
            shallow
        )
    const { epgData, setEpg } = useEPGStore(
        (state) => ({
            epgData: state.epgData,
            setEpg: state.setEpg,
        }),
        shallow
    )
    const setClearVod = useVODStore(selectSetClearVod)
    const { takeConfigIfNeed } = useConfig()
    const { actualProfile, isCacheChecked, setIsCacheChecked } = useAppStore()
    const { setRowItems } = useVodItems('')
    const { getChannelsList } = useTVRepository()
    const { getFavoritesChannels } = useFavoritesChannels()

    const {
        createEpg,
        filterEpgList,
        sortEpgList,
        mergeEpgArrays,
        prepareEPGforSetToState,
    } = useEPGRepository()

    const { getCategoriesForFiltering } = useCategoriesForFiltering()

    useSetCategoryIfAllGenres()

    const { setEpgToStoreAndCache, setTvToStoreAndCache } =
        useSetTVAndEpgToStateAndCache()

    const token: string | null = getFromLocalStorage(sessionTokenLsKeyword)

    const clearCacheOnAppInit = async () => {
        const lastTime = getFromLocalStorage(lastTimeStart)
        const dateNow = Date.now()

        if (lastTime && dateNow - Number(lastTime) > MILIS_6h) {
            clearCache()
            setClearVod()
            clearTvStore()
            setRowItems([])

            removeFromLocalStorage(sessionTokenLsKeyword)
            removeFromLocalStorage(refreshTokenKeyword)
            removeFromLocalStorage(IS_EPG_CACHE_CHECKED)
            removeFromLocalStorage(IS_TV_CACHE_CHECKED)
            saveToLocalStorage(lastTimeStart, `${dateNow}`)
        } else if (!lastTime) {
            saveToLocalStorage(lastTimeStart, `${dateNow}`)
        }
    }

    async function fetchChannels() {
        try {
            const channelsList = await getChannelsList(token)
            if (channelsList) {
                const sortedChannels = channelsList.sort(
                    (channelA, channelB) =>
                        Number(channelA.lcn) - Number(channelB.lcn)
                )
                setTvToStoreAndCache(sortedChannels)
            }
            return channelsList
        } catch (error) {
            getErrorMessage('Error fetching channels:', error)
        }
    }

    const initializeChannelsAndEpgData = async () => {
        if (!channelsData.length) {
            await initializeCacheAndTv()
        } else if (channelsData.length && isStoreHydrated) {
            await initializeCacheAndEpg()
        }
    }

    const initializeCacheAndTv = async () => {
        try {
            const cachedTV = await getTvFromCache()
            if (cachedTV?.length) {
                setChannelsData(cachedTV)
            } else {
                await fetchChannels()
            }
        } catch (error) {
            getErrorMessage('Error during cache or fetch initialization', error)
        } finally {
            setIsCacheChecked(true)
            saveToLocalStorage(IS_TV_CACHE_CHECKED, 'true')
        }
    }

    const initializeCacheAndEpg = async () => {
        try {
            const cachedEpg = await getEpgFromCache()
            const isEpgCacheChecked = JSON.parse(
                getFromLocalStorage(IS_EPG_CACHE_CHECKED) || ''
            )
            if (cachedEpg?.length && isEpgCacheChecked) {
                setEpg(cachedEpg)
            } else if (isEpgCacheChecked) {
                const programs = await getProgramsForAllChannels()
                if (programs?.length) {
                    await setupEpgDataFromChannels(channelsData, programs)
                } else {
                    console.log('No cache and no programs found')
                }
            }
        } catch (error) {
            console.error('Error during cache or fetch initialization', error)
        } finally {
            setIsCacheChecked(true)
            saveToLocalStorage(IS_EPG_CACHE_CHECKED, 'true')
        }
    }

    const getPresentFollowing = async () => {
        const programs = await getProgramsForAllChannels()
        if (!programs?.length) return
        await setupEpgDataFromChannels(channelsData, programs)
    }

    const setupEpgDataFromChannels = async (
        channelsList: Channel[],
        programs: RawEpgObject[]
    ) => {
        setEpgToStoreAndCache([])
        const tvPresentFollowing = createEpg(channelsList, programs)
        const initialEpg = createInitialExtendedEpg(channelsList)
        const leftDirectionArray = channelsList.slice(
            -MAX_CHANNELS_FETCH_AMOUNT
        )
        const rightDirectionArray = channelsList.slice(
            0,
            MAX_CHANNELS_FETCH_AMOUNT
        )

        const epgListEnd = await prepareEPGforSetToState(
            leftDirectionArray,
            createEpg,
            filterEpgList,
            sortEpgList
        )
        const epgListBeginning = await prepareEPGforSetToState(
            rightDirectionArray,
            createEpg,
            filterEpgList,
            sortEpgList
        )

        let epgList: ExtendedEpgElement[] = []
        if (epgListEnd && epgListBeginning) {
            epgList = [...epgListEnd, ...epgListBeginning]
        }

        if (epgList.length && tvPresentFollowing) {
            const epg = mergeEpgArrays(epgList, tvPresentFollowing)
            const fullEpg = mergeEpgArrays(epg, initialEpg).sort(
                (itemA, itemB) => itemA.channelNumber - itemB.channelNumber
            )
            setEpgToStoreAndCache(fullEpg)
        }
    }

    useEffect(() => {
        if (!isStoreHydrated) return

        async function initApp() {
            await clearCacheOnAppInit()

            await initializeChannelsAndEpgData()
            await takeConfigIfNeed()
        }

        initApp()
    }, [
        isStoreHydrated,
        channelsData.length,
        isCacheChecked,
        isStoreHydrated,
        actualProfile?.id,
    ])

    useEffect(() => {
        setIsCacheChecked(false)
        saveToLocalStorage(IS_EPG_CACHE_CHECKED, 'false')
        saveToLocalStorage(IS_TV_CACHE_CHECKED, 'false')
        clearCache()
    }, [actualProfile?.id])

    useEffect(() => {
        const intervalId = setInterval(() => {
            getPresentFollowing()
        }, PRESENT_FOLLOWING_REFRESH_INTERVAL)

        return () => clearInterval(intervalId)
    }, [])

    useEffect(() => {
        if (!channelsData.length) return

        setIsComponentsReadyToRender(true)
        getCategoriesForFiltering()
        getFavoritesChannels()
        if (!epgData.length) initializeCacheAndEpg()
    }, [channelsData.length, epgData.length])

    // Here we reset app cache automatically
    useCacheAutoRefresh()

    return (
        <ComponentsRenderContext.Provider
            value={useMemo(
                () => ({
                    isComponentsReadyToRender,
                    getCategoriesForFiltering,
                }),
                [isComponentsReadyToRender]
            )}>
            {children}
        </ComponentsRenderContext.Provider>
    )
}

export const useComponentsRender = () => useContext(ComponentsRenderContext)
